// The old app's working state (its global S) and the small helpers every calculation uses.
// Ported line for line from v1 (portfolio-ledger.html, v17): blank(), sanitize(), cv(), assetCcy(),
// needsFx() and the commodity unit helpers. Records stay plain objects, as in v1.
/* eslint-disable @typescript-eslint/no-explicit-any */
import { today } from './clock';

export type Ccy = 'SGD' | 'USD';
export const CCYS: Ccy[] = ['SGD', 'USD'];
export const okC = (c: unknown): c is Ccy => CCYS.includes(c as Ccy);

export interface Fx { rate: number; date: string; src: string; at: number | null }
export interface Price { price: number; ccy: Ccy; at: number | null; src: string; prev?: number | null; pd?: string | null }
export interface State {
  v: number;
  display: Ccy;
  fx: Fx | null;
  txns: any[];
  income: any[];
  stakes: any[];
  cash: any[];
  upcoming: any[];
  history: any[];
  dismissed: string[];
  inboxDone: any[];
  inboxLast: any;
  prices: Record<string, Price>;
  assets: Record<string, any>;
}

export function blank(): State {
  return { v: 2, display: 'SGD', fx: null, txns: [], income: [], stakes: [], cash: [], upcoming: [], history: [], dismissed: [], inboxDone: [], inboxLast: null, prices: {}, assets: {} };
}

export function sanitize(d: any): State {
  const b = blank(); if (!d || typeof d !== 'object') return b;
  const legacy: Ccy = okC(d.currency) ? d.currency : 'SGD';
  b.display = okC(d.display) ? d.display : legacy;
  if (d.fx && +d.fx.rate > 0) b.fx = { rate: +d.fx.rate, date: d.fx.date || '', src: d.fx.src || 'manual', at: d.fx.at || null };
  const fix = (r: any) => Object.assign({}, r, { ccy: okC(r.ccy) ? r.ccy : legacy });
  b.txns = (Array.isArray(d.txns) ? d.txns : []).map(fix);
  b.income = (Array.isArray(d.income) ? d.income : []).map(fix);
  b.stakes = Array.isArray(d.stakes) ? d.stakes : [];
  b.cash = (Array.isArray(d.cash) ? d.cash : []).map(fix);
  b.history = (Array.isArray(d.history) ? d.history : []).filter((h: any) => h && /^\d{4}-\d{2}-\d{2}$/.test(h.d) && +h.sgd >= 0);
  b.upcoming = Array.isArray(d.upcoming) ? d.upcoming : []; b.dismissed = Array.isArray(d.dismissed) ? d.dismissed : [];
  b.inboxDone = Array.isArray(d.inboxDone) ? d.inboxDone : []; b.inboxLast = d.inboxLast && typeof d.inboxLast === 'object' ? d.inboxLast : null;
  if (d.prices && typeof d.prices === 'object') for (const [k, v] of Object.entries<any>(d.prices)) { if (v && v.price != null && v.price !== '') b.prices[k] = Object.assign({ price: +v.price, ccy: okC(v.ccy) ? v.ccy : legacy, at: v.at || null, src: v.src || 'manual' }, v.prev > 0 ? { prev: +v.prev, pd: v.pd || null } : {}); }
  if (d.assets && typeof d.assets === 'object') b.assets = d.assets;
  return b;
}

export const N = (x: unknown): number => { const n = parseFloat(x as string); return isFinite(n) ? n : 0; };

/** S.fx.rate = SGD per 1 USD. Every amount is converted to the display currency at the latest rate. */
export function cv(S: State, n: unknown, ccy?: string): number {
  const x = N(n); if (!ccy || ccy === S.display) return x; const r = S.fx && S.fx.rate; if (!r) return x; return ccy === 'USD' ? x * r : x / r;
}
/** cv() into SGD whatever the display currency (v1's cv2sgd). */
export function cvSgd(S: State, n: unknown, ccy?: string): number { return cv({ ...S, display: 'SGD' }, n, ccy); }

export const keyOf = (type: string, sym: string) => type + ':' + sym;
export const meta = (S: State, k: string): any => S.assets[k] || {};

export function assetCcy(S: State, type: string, sym: string): Ccy {
  const k = keyOf(type, sym), m = meta(S, k); if (okC(m.ccy)) return m.ccy;
  const last = [...S.txns, ...S.income].filter((r) => r.type === type && r.sym === sym).sort((a, b) => (a.date < b.date ? 1 : -1))[0];
  if (last) return last.ccy;
  if (type === 'crypto') return 'USD';
  if (type === 'metal') return S.display;
  return /\.(SI|SG|SGX)$/i.test(sym) || (/^[A-Z]\d{2}$|^[A-Z0-9]{3}$/.test(sym) && /\d/.test(sym)) ? 'SGD' : 'USD';
}
export function needsFx(S: State): boolean {
  return [...S.txns, ...S.income, ...S.cash].some((r) => r.ccy !== S.display) || Object.values(S.prices).some((p) => p.ccy !== S.display);
}

// ---------- commodities ----------
export const METALS: Record<string, string> = { XAU: 'Gold', XAG: 'Silver', XPT: 'Platinum', XPD: 'Palladium' };
export const UNITS: Record<string, [number, string, string]> = { oz: [1, 'troy oz', 'oz'], g: [1 / 31.1034768, 'grams', 'g'], kg: [1000 / 31.1034768, 'kilograms', 'kg'] };
export const uf = (u?: string) => (UNITS[u as string] || UNITS.oz)[0];
export const tq = (t: any) => (t.type === 'metal' ? N(t.qty) * uf(t.unit) : N(t.qty));
// Bonds (v2): quantity is the face amount and price is per 100 of face, so per 1 of face here.
export const tp = (t: any) => (t.type === 'metal' ? N(t.price) / uf(t.unit) : t.type === 'bond' ? N(t.price) / 100 : N(t.price));
export const nm = (type: string, sym: string) => (type === 'metal' ? METALS[sym] || sym : sym);
export const metalUnit = (S: State, sym: string) => { const m = meta(S, 'metal:' + sym); return UNITS[m.unit] ? m.unit : 'oz'; };
export const CLS: Record<string, string> = { stock: 'Stock', crypto: 'Crypto', metal: 'Commodity', bond: 'Bond' };

/** A stock is treated as SGX-listed (price via the SGX feed, no default withholding tax) when its
 *  currency is SGD, its source is the sheet / SGX feed (or the old Alpha Vantage setting), or its
 *  ticker has a digit. */
export function isSgx(S: State, p: { key: string; sym: string }): boolean {
  const m = meta(S, p.key); return assetCcy(S, 'stock', p.sym) === 'SGD' || m.provider === 'av' || m.provider === 'sheet' || /\d/.test(p.sym);
}

export { today };
